import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import { ProductSettingsModel, PromotionModel } from "@insite/client-framework/Types/ApiModels";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import CartLineNotes, { CartLineNotesStyles } from "@insite/content-library/Widgets/Cart/CartLineNotes";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import Icon from "@insite/mobius/Icon";
import Trash2 from "@insite/mobius/Icons/Trash2";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useLaunchDarklyFlags from "@MasterBluePrint/Common/Hooks/useLaunchDarklyFlags";
import { ValidateMSQWithOrderedQty } from "@MasterBluePrint/Components/MsqValidation";
import CalifPropIconNew from "@MasterBluePrint/Icons/CalifPropIconNew";
import HazardousIcon from "@MasterBluePrint/Icons/Hazardous";
import RoHsIcon from "@MasterBluePrint/Icons/RoHs";
import ShippingIconCannotShipAir from "@MasterBluePrint/Icons/ShippingIconCannotShipAir";
import ShippingIconDryIce from "@MasterBluePrint/Icons/ShippingIconDryIce";
import ShippingIconNextDayAir from "@MasterBluePrint/Icons/ShippingIconNextDayAir";
import CartProductPartNumbers, { CartProductPartNumbersStyles } from "@MasterBluePrint/Overrides/Components/CartProductPartNumbers";
import ProductAvailability, { ProductAvailabilityStyles } from "@MasterBluePrint/Overrides/Components/ProductAvailability";
import CartLineQuantity, { CartLineQuantityStyles } from "@MasterBluePrint/Overrides/Widgets/Cart/CartLineQuantity";
import { getCurrentWebsiteCountry } from "@MasterBluePrint/Services/CurrentWebsiteCountryService";
import { isMobileDevice } from "@MasterBluePrint/Overrides/Common/Utilities/isMobileDevice";
import React, { FC, useContext, useEffect, useState } from "react";
import { connect, HandleThunkActionCreator, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    promotions: PromotionModel[];
    productSettings: ProductSettingsModel;
    showInventoryAvailability?: boolean;
    showLineNotes?: boolean;
    updateCartLine: HandleThunkActionCreator<typeof updateCartLine>;
    removeCartLine: HandleThunkActionCreator<typeof removeCartLine>;
    showRemoveAction?: boolean;
    hideAddToList?: boolean;
    extendedStyles?: CartLineCardExpandedStyles;
    showShippingRestrictionIcon: boolean;
    showHazardousGoodIcon: boolean;
    showCalifPropIcon: boolean;
    showRoHsIcon: boolean;
    showNcNrIcon: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    wishListSettings: getSettingsCollection(state).wishListSettings,
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
});

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
    loadCurrentCart,
};

type Props = OwnProps &
    HasCartLineContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CartLineCardExpandedStyles {
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    cartLineInfoGridItem?: GridItemProps;
    cartLineInfoContainer?: GridContainerProps;
    productInfoGridItem?: GridItemProps;
    productInfoContainer?: GridContainerProps;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productBrand?: ProductBrandStyles;
    productDescriptionGridItem?: GridItemProps;
    productDescription?: ProductDescriptionStyles;
    configurationGridItem?: GridItemProps;
    configurationOptionText?: TypographyProps;
    cartProductPartNumbersGridItem?: GridItemProps;
    cartProductPartNumbers?: CartProductPartNumbersStyles;
    quantityAndExtendedUnitNetPriceGridItem?: GridItemProps;
    quantityAndExtendedUnitNetPriceContainer?: GridContainerProps;
    quantityGridItem?: GridItemProps;
    quantity?: CartLineQuantityStyles;
    extendedUnitNetPriceGridItem?: GridItemProps;
    extendedUnitNetPriceText?: TypographyProps;
    vatLabelText?: TypographyPresentationProps;
    subtotalWithoutVatText?: TypographyPresentationProps;
    cartLineErrorMessageGridItem?: GridItemProps;
    cartLineErrorMessageText?: TypographyProps;
    productPriceAndAvailabilityGridItem?: GridItemProps;
    productPrice?: ProductPriceStyles;
    promotionNameText?: TypographyProps;
    productAvailability?: ProductAvailabilityStyles;
    addToListLink?: LinkPresentationProps;
    costCodeGridItem?: GridItemProps;
    costCodeLabelText?: TypographyPresentationProps;
    costCodeSelect?: SelectPresentationProps;
    costCodeText?: TypographyPresentationProps;
    cartLineNotesGridItem?: GridItemProps;
    cartLineNotes?: CartLineNotesStyles;
    removeCartLineGridItem?: GridItemProps;
    removeCartLineIcon?: IconPresentationProps;
    callText?: TypographyProps;
    hazmatGridItem?: GridItemProps;
    iconGridItem?: GridItemProps;
    nonCancellableNonReturnableContainer?: InjectableCss;
    nonCancellableNonReturnableText?: InjectableCss;
    nonCancellableNonReturnableAnchor?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    cartLineHazmatMessageGridItem?: GridItemProps;
    cartLineHazmatMessage?: TypographyPresentationProps;
    cartLineShipFreightGridItem?: GridItemProps;
    cartLineShipFreightMessage?: TypographyPresentationProps;
}

export const cartLineCardExpandedStyles: CartLineCardExpandedStyles = {
    container: {
        gap: 20,
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding: 28px 1.25rem;
            background: #fff;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            max-width: 100%;
                            padding: .625rem;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )
            };
        `,
    },
    productImageGridItem: {
        css: css`
            justify-content: center;
            flex-basis: auto;
            max-width: initial;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: .625rem;
                        width: 100%;
                        justify-content: left;
                        flex-direction: column;
                        .mobile-product-heading{
                            margin-bottom: 1rem;
                            .brand-name {
                                font-size: .875rem;
                                letter-spacing: 0;
                                line-height: 1.25rem;
                            }
                            button {
                                color: #002554;
                                font-family: 'Roboto', sans-serif;
                                font-size: 1rem;
                                font-weight: 500;
                                letter-spacing: 0;
                                line-height: 1.25rem;

                                span {
                                    color: #002554;
                                    font-family: Roboto;
                                    font-size: 1rem; 
                                }
                            }
                        }
                        @media print {
                            width: auto;
                            min-width: 180px;
                            flex-direction: row;

                            .mobile-product-heading {
                                display: none;
                            }
                        }
                    `,
                    css`
                        font-size: .625rem;
                        width: 100%;
                        justify-content: left;
                        flex-direction: column;
                        .mobile-product-heading{
                            margin-bottom: 1rem;
                            .brand-name {
                                font-size: .875rem;
                                letter-spacing: 0;
                                line-height: 1.25rem;
                            }
                            button {
                                color: #002554;
                                font-family: 'Roboto', sans-serif;
                                font-size: 1rem;
                                font-weight: 500;
                                letter-spacing: 0;
                                line-height: 1.25rem;

                                span {
                                    color: #002554;
                                    font-family: Roboto;
                                    font-size: 1rem; 
                                }
                            }
                        }
                        @media print {
                            width: auto;
                            min-width: 180px;
                            flex-direction: row;

                            .mobile-product-heading {
                                display: none;
                            }
                        }
                    `,
                    css`
                        font-size: .625rem;
                        width: 100%;
                        justify-content: left;
                        flex-direction: column;
                        @media print {
                            width: auto;
                            min-width: 180px;
                            flex-direction: row;

                            .mobile-product-heading {
                                display: none;
                            }
                        }
                        .mobile-product-heading{
                            margin-bottom: 1rem;
                            .brand-name {
                                font-size: .875rem;
                                letter-spacing: 0;
                                line-height: 1.25rem;
                            }
                            button {
                                color: #002554;
                                font-family: 'Roboto', sans-serif;
                                font-size: 1rem;
                                font-weight: 500;
                                letter-spacing: 0;
                                line-height: 1.25rem;

                                span {
                                    color: #002554;
                                    font-family: Roboto;
                                    font-size: 1rem; 
                                }
                            }
                        }
                    `,
                    css`
                        font-size: .625rem;
                        .mobile-product-heading{
                            display: none;
                        }
                        @media print {
                            width: auto;
                            min-width: 180px;
                            flex-direction: row;

                            .mobile-product-heading {
                                display: none;
                            }
                        }
                    `,
                    css`
                        font-size: .625rem;
                        .mobile-product-heading{
                            display: none;
                        }
                        @media print {
                            width: auto;
                            min-width: 180px;
                            flex-direction: row;

                            .mobile-product-heading {
                                display: none;
                            }
                        }
                    `,
                ])}
        `,
    },
    cartLineInfoGridItem: {
        width: [12, 12, 12, 7, 8],
        css: css`           
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            overflow: hidden;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )
            };
        `,
    },
    cartLineInfoContainer: {
        gap: 20,
        css: css`
            position: relative;
            
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                        max-width: 100%;
                    `,
                        null,
                        null,
                    ],
                    "max",
                )
            };
        `,
    },
    productInfoGridItem: {
        width: [12, 12, 12, 7, 7],
        css: css`
            &.name-wrapper {
                div > div > div {
                    .brand-name {
                        color: #002554;
                        font-family: Roboto;
                        font-size: .875rem;
                        letter-spacing: 0;
                        line-height: 1.5rem;
                        text-transform: uppercase;
                    }

                    button {
                        color: #002554;
                        font-family: Roboto;
                        font-size: 1rem;
                        font-weight: 500;
                        letter-spacing: 0;
                        line-height: 1.25rem;

                        span {
                            color: #002554;
                        }
                    }
                }
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            &.name-wrapper {
                                border-top: 1px solid #E1E1E1;

                                > div {
                                    max-width: 100%;
                                }
                                
                                @media print {
                                    border: none;
                                }
                            }
                            .desktop-product-heading {
                                display: none;
                            }
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    productInfoContainer: { gap: 12 },
    productBrandAndDescriptionGridItem: {
        css: css`
            flex-direction: column;
        `,
        width: 12,
    },
    productDescriptionGridItem: {
        width: 12,
    },
    configurationGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    cartProductPartNumbersGridItem: { width: 12 },
    quantityAndExtendedUnitNetPriceGridItem: { width: 12 },
    quantityGridItem: {
        width: 12,
        css: css`
            .input-container {
                margin: .9375rem 0;
            }
            .input-container input[data-test-selector="cartline_qty"] {
                width: 9rem !important;
                min-width: 9rem !important;
                color: #333F47 !important;
                font-family: 'Roboto', sans-serif;
                font-size: .875rem !important;
                -webkit-letter-spacing: .16px;
                -moz-letter-spacing: .16px;
                -ms-letter-spacing: .16px;
                letter-spacing: .16px;
                text-align: center;
                padding: 0 2.5rem !important;
                -moz-appearance: textfield !important;
                background: #fff !important;
            }

            .input-container input::-webkit-outer-spin-button,
            .input-container input::-webkit-inner-spin-button {
                display: none;
                -webkit-appearance: none;
                margin: 0;
            }
            @media screen and (min-width: 768px) and (max-width: 1399px) {
                .input-container > div > div {
                    margin: .625rem 0 !important;
                }
                .input-container {
                    margin: .625rem 0 !important;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                        .input-container > div > div {
                            margin: .625rem 0 !important;
                        }
                        .input-container {
                            margin: .625rem 0 !important;
                        }
                    `,
                        null,
                        null,
                    ],
                    "max",
                )}
            
            @media print {
                max-width: 143px;
            }
        `,
    },
    extendedUnitNetPriceGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
            align-items: flex-start;
            justify-content: flex-end;
            padding-left: 0;
        `,
    },
    extendedUnitNetPriceText: {
        weight: "bold",
        css: css`
            .product-price {
                opacity: 0.7;
                color: #C63527;
                font-size: 1.25rem;
                font-weight: bold;
                letter-spacing: -0.38px;
                line-height: 1.4375rem;
            }
            
            
            span.unit-label {
                padding-left: .625rem;
                white-space: nowrap;
                color: #424B5A;
                text-transform: lowercase;
                font-size: 1rem !important;
                margin-right: 0.8rem;
                font-weight: 400;
            }
        `,
    },
    callText: {
        weight: "bold",
    },
    vatLabelText: {
        size: 12,
    },
    subtotalWithoutVatText: {
        weight: "bold",
        css: css`
            margin-top: .31250625rem;
        `,
    },
    cartLineErrorMessageGridItem: { width: 12 },
    cartLineErrorMessageText: { color: "danger" },
    productPriceAndAvailabilityGridItem: {
        width: [12, 12, 12, 5, 5],
        css: css`
            flex-direction: column;

            .price-section {
                p {
                    margin-top: .625rem;
                }
                legend {
                    display: none;
                }

                .vat-field {
                    display: none;
                }

                span[data-test-selector="productPrice_unitNetPrice"] {
                    opacity: 0.7;
                    font-size: 1.25rem;
                    font-weight: bold;
                    letter-spacing: -0.38px;
                    line-height: 1.4375rem;
                }


                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                           margin-bottom: 0;
                        `,
                        null,
                    ],
                    "max",
                )}

            }
            .adjust-spacing {
                div > div > div {
                    margin-top: 0;
                }
                .stock-container {
                    span {
                        color: #333F48;
                        font-family: 'Lato', sans-serif;
                        font-size: .875rem;
                        letter-spacing: 0;
                        line-height: 1.25rem;
                    }    
                }

                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                        border-top: 1px solid #E1E1E1;
                        border-bottom: 1px solid #E1E1E1;
                        padding: 8px .9375rem 8px .9375rem;
                        margin: 0 -4px 12px -10px;
                        max-width: 106%;
                        width: 106%;

                        @media print {
                            border: none;
                            padding: .31250625rem 0;
                            margin: 0 0 .75rem 0;
                            max-width: 100%;
                            width: 100%;
                        }
                    `,
                        null,
                        null,
                    ],
                    "max",
                )};
            }
        `,
    },
    addToListLink: {
        css: css`
            display: flex;

            .icon-add {
                svg {
                    width: 1.25rem;
                    height: 1.25rem;
                    stroke: #333F48;
                    stroke: #002554;
                }
            }
            .add-label {
                color: #333F48;
                color: #002554;
                font-family: 'Lato', sans-serif;
                font-size: .875rem;
                letter-spacing: 0.16px;
                line-height: 1.5rem;
                text-align: center;
                margin: 0 auto;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            margin-top: 1.25rem;
                            .icon-add {
                                display: block;
                                margin: 0 auto;
                            }
                            .add-label {
                                display: block;
                            }
                        `,
                        null,
                    ],
                    "min",
                )}
                
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            .add-label {
                                position: relative;
                                top: -4px;
                            }
                        `,
                        null,
                    ],
                    "max",
                )}    
        `,
    },
    costCodeGridItem: {
        width: [12, 12, 12, 6, 6],
        css: css`
            flex-direction: column;
        `,
    },
    costCodeLabelText: {
        css: css`
            margin-bottom: .625rem;
        `,
    },
    cartLineNotesGridItem: { width: 12 },
    removeCartLineGridItem: {
        width: [12, 12, 12, 1, 1],
        css: css`
            align-items: center;
            flex-direction: column;
            justify-content: flex-start;
            order: 0;
            .remove-label {
                color: #333F48;
                color: #002554;
                font-family: 'Lato', sans-serif;
                font-size: .875rem;
                letter-spacing: 0.16px;
                line-height: 1.5rem;
                text-align: center;
                margin: 0 auto;
            }
            @media screen and (min-width: 990px) {
                flex-basis: 12%;
                max-width: 12%;
                
                button{
                    margin-top: 1.25rem;
                }
                
                @media print {
                    width: 0;
                    display: none !important;
                }
            }
            @media screen and (max-width: 990px) {
                flex-direction: row;
                justify-content: space-between;

                .remove-label {
                    position: relative;
                    top: -4px;
                }

                .loading-spinner {
                    display: inline-block;
                    margin: 0 0.5rem 0 0;
                }
            }
            
            @media print {
                width: 0;
                display: none !important;
            }
        `,

    },
    removeCartLineIcon: {
        src: Trash2,
        css: css`
            svg {
                width: 1.25rem;
                height: 1.25rem;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            display: block;
                            margin: 0 auto;
                        `,
                        null,
                    ],
                    "min",
                )}
        `,
    },
    hazmatGridItem: {
        width: 12,
        css: css`
            background: #F7F9FA;
            padding: 6px .625rem;
            color: #333F48;
            font-family: 'Lato', sans-serif;
            font-size: .875rem;
            letter-spacing: 0;	
        `,
    },
    iconGridItem: {
        width: [6, 6, 12, 12, 12],
        css: css`
            background: #F7F9FA;
            padding: 6px .625rem;
            color: #333F48;
            font-family: 'Lato', sans-serif;
            font-size: .875rem;
            letter-spacing: 0;
            @media screen and (min-width: 1400px) {
                flex-basis: 50%;
                max-width: 50%;
            }
            @media screen and (max-width: 1399px) {
                flex-basis: 50%;
                max-width: 50%;
            }	
        `,
    },
    nonCancellableNonReturnableContainer: {
        css: css`
            font-weight: 600;
            font-size: 0.85rem;
            margin-bottom: 0.2em;
            text-align: left;
            color: #333F48;
            @media screen and (min-width: 1400px) {
                max-width: 166px;
                max-width: 170px;
                white-space: normal;
                margin-bottom: .625rem;
            }
            @media screen and (max-width: 1399px) {
                padding: .9375rem .9375rem 0 0;
                position: relative;
                margin-top: -27px;
                margin-bottom: 0.3rem;
            }
            @media screen and (max-width: 990px) {
                position: absolute;
                bottom: 1.5625rem;
                left: 160px;
                padding-right: 0;
            }
        `,
    },
    nonCancellableNonReturnableText: {
        css: css`
        color: #333F48;
        font-family: 'Lato', sans-serif;
        font-size: .875rem;
        font-style: italic;
        letter-spacing: 0.04px;
        line-height: 19px;
        text-align: center;
        text-align: left;
        font-weight: normal;
        `,
    },
    nonCancellableNonReturnableAnchor: {
        css: css`
        color: #333F48;
        font-size: .875rem;
        font-style: italic;
        letter-spacing: 0.04px;
        line-height: 19px;
        text-align: center;
        text-align: left;
        font-family: 'Lato', sans-serif;
        font-weight: normal;
        &:hover {
            text-decoration: underline !important;
            color: #333F48 !important;
        }
        span {
            font-size: .875rem;
            color: #333F48 !important;

            &:hover {
                text-decoration: underline !important;
                color: #333F48 !important;
            }
        }

        `,
    },
    spinner: {
        css: css`
            width: 0.9rem;
            height: 0.9rem;
            margin: 0.3rem auto;
            display: block;
        `,
    },
    cartLineHazmatMessageGridItem: {
        width: 12,
        css: css`
            padding:5px 0 0;
        `,
    },
    cartLineHazmatMessage: {
        css: css`
            width:100%;
            background: #f9eae9;
            border:1px solid #df9088;
            border-left:3px solid #c63527;
            margin-bottom:5px;
            p {
                color: #222;
                font-size:80%;
                line-height:1.35;
                letter-spacing:.01rem;
                padding:3px 6px
            }
            
        `,
    },
    cartLineShipFreightGridItem: {
        width: 12,
        css: css`
            padding:5px 0 0;
        `,
    },
    cartLineShipFreightMessage: {
        css: css`
            width:100%;
            background: #e5f5fc;
            border:1px solid #72c8ed;
            border-left:3px solid #2060ad;
            margin-bottom:5px;
            p {
                color: #222;
                font-size:80%;
                line-height:1.35;
                letter-spacing:.01rem;
                padding:3px 6px
            }
            
        `,
    },
};
const CartLineCardExpanded: FC<Props> = ({
    cart,
    cartLine,
    promotions,
    productSettings,
    showInventoryAvailability,
    showLineNotes,
    updateCartLine,
    removeCartLine,
    extendedStyles,
    showRemoveAction,
    wishListSettings,
    setAddToListModalIsOpen,
    addToWishList,
    hideAddToList,
    enableVat,
    vatPriceDisplay,
    showShippingRestrictionIcon,
    showHazardousGoodIcon,
    loadCurrentCart,
    showCalifPropIcon,
    showRoHsIcon,
    showNcNrIcon,
}) => {
    const [currentWSCountry, setCurrentWebsiteCountry] = useState("");
    useEffect(() => {
        getCurrentWebsiteCountry().then(response => {
            setCurrentWebsiteCountry(response);
        });
    }, []);
    const toasterContext = useContext(ToasterContext);
    const qtyOrderedChangeHandler = (qtyOrdered: number) => {
        if (qtyOrdered !== cartLine.qtyOrdered) {
            if (qtyOrdered <= 0) {
                removeCartLine({ cartLineId: cartLine.id });
            } else {
                if (!ValidateMSQWithOrderedQty(cartLine, qtyOrdered)) {
                    const multipleSaleQty = cartLine?.properties?.multipleSaleQty
                        ? Number(cartLine?.properties?.multipleSaleQty)
                        : 0;
                    document
                        .querySelector('[data-test-selector="cartTotal_Checkout"]')
                        ?.setAttribute("disabled", "disabled");
                    document
                        .querySelector('[data-test-selector="cartTotal_SubmitQuote"]')
                        ?.setAttribute("disabled", "disabled");
                    document
                        .querySelector(`[data-test-selector="addtolist_${cartLine.id}"]`)
                        ?.setAttribute("style", "display:none");

                    toasterContext.addToast({
                        body: siteMessage("Core_MultipleSaleQtyErrorSpire")
                            .toString()
                            .replace("{0}", `${multipleSaleQty}`),
                        messageType: "danger",
                    });

                    return;
                }

                updateCartLine({
                    cartLine: {
                        ...cartLine,
                        qtyOrdered,
                    },
                });
            }
        }
    };

    const costCodeChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const costCode = event.currentTarget.value;
        if (costCode !== cartLine.costCode) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    costCode,
                },
            });
        }
    };

    const notesChangeHandler = (notes: string) => {
        if (notes !== cartLine.notes) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    notes,
                },
                shouldNotReloadPromotions: true,
                onSuccess: () => {
                    toasterContext.addToast({
                        body: translate("Line Notes Updated"),
                        messageType: "success",
                    });
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.();
                    }
                },
            });
        }
    };

    const [removeLoading, setremoveLoading] = useState(false);
    const [isQuoteCart, setIsQuoteCart] = useState(false);
    useEffect(() => {
        if (cart.properties?.isquote === "true") {
            setIsQuoteCart(true);
        }
    }, [cart]);

    const removeCartLineClickHandler = () => {
        setremoveLoading(true);
        if (!showRemoveAction) {
            return;
        }
        removeCartLine({ cartLineId: cartLine.id });
    };

    const addToListClickHandler = () => {

        if (!wishListSettings) {
            return;
        }

        const productInfo = {
            productId: cartLine.productId!,
            qtyOrdered: cartLine.qtyOrdered!,
            unitOfMeasure: cartLine.unitOfMeasure,
        };

        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
        loadCurrentCart({ shouldLoadFullCart: true, getCartLines: true });
    };
    useEffect(() => {
        // if (modalIsOpen && !wishListsDataView.value && !wishListsDataView.isLoading) {
        //     loadWishLists(getWishListsParameter);
        // }
    }, [cartLine, addToListClickHandler]);
    const sumQtyPerUom = cart.cartLines!.reduce((sum, current) => {
        return current.productId === cartLine.productId
            ? sum + current.qtyPerBaseUnitOfMeasure * current.qtyOrdered!
            : sum;
    }, 0);

    const errorMessages: React.ReactNode[] = [];
    if (showInventoryAvailability && cartLine.hasInsufficientInventory && !isOutOfStock(cartLine)) {
        const tooManyRequestedMessage = siteMessage(
            "Cart_ToManyQtyRequested",
            cartLine.qtyOnHand.toLocaleString(),
            sumQtyPerUom.toLocaleString(),
        );
        errorMessages.push(tooManyRequestedMessage);
    }

    if (cartLine.isRestricted) {
        errorMessages.push(translate("Restricted product"));
    }

    if (!cartLine.isActive) {
        errorMessages.push(translate("Inactive Product"));
    }

    let shippingIconText = "";
    let isShowShippingIconText = true;
    let shippingIcon = ShippingIconNextDayAir;

    if (cartLine.properties?.shippingClassification === "MP") {
        shippingIconText = translate("Next Day Air Only");
        shippingIcon = ShippingIconNextDayAir;
    }

    else if (cartLine.properties?.shippingClassification?.toLowerCase() === "md") {
        shippingIconText = translate("Requires Dry Ice");
        shippingIcon = ShippingIconDryIce;
    }

    else if (
        cartLine.properties?.shippingClassification === "MG" ||
        cartLine.properties?.shippingClassification === "MC" ||
        cartLine.properties?.shippingClassification === "MB"
    ) {
        shippingIconText = translate("Cannot Ship Air");
        shippingIcon = ShippingIconCannotShipAir;
    }

    else {
        isShowShippingIconText = false;
    }
    const hazardousIconText = cartLine.properties?.isHazardousGood === "TRUE" ? translate("Hazardous") : "";
    const califPropIconText = translate("Calif. Prop 65");
    const roHsIconText = translate("RoHS");
    const [styles] = React.useState(() => mergeToNew(cartLineCardExpandedStyles, extendedStyles));

    const featureFlags: string[] = ["TEUS-8066", "TEUS-8247"];
    const flagValues = useLaunchDarklyFlags({ featureFlags });
    const moveButtonsToBottom = flagValues["TEUS-8066"] && isMobileDevice();
    const enableMobileCartLineEasierDesign = flagValues["TEUS-8247"];

    return (
        <GridContainer
            {...styles.container}
            data-test-selector={`cartline_expanded_${cartLine.productId}_${cartLine.unitOfMeasure}`}
        >
            <GridItem {...styles.removeCartLineGridItem} style={{
                order: moveButtonsToBottom ? 3 : 0,
            }}>
                {cartLine?.properties?.isProductAddedWishList === "true" ?
                    (<Typography as="span" {...styles.addToListLink}>
                        <span>
                            <Icon src="Check" className="icon-add" />
                            <Typography as="span" className="add-label">{translate("Added to List")}</Typography>
                        </span>
                    </Typography>
                    ) :
                    !hideAddToList && cartLine.canAddToWishlist && (
                        <Link
                            {...styles.addToListLink}
                            data-test-selector={`addtolist_${cartLine.id}`}
                            onClick={addToListClickHandler}
                            disabled={isQuoteCart}
                            id="cart-btn-add-to-list"
                        >
                            <Icon src="Plus" className="icon-add" />
                            <Typography as="span" className="add-label">{translate("Add to List")}</Typography>
                        </Link>
                    )}

                {showRemoveAction && (
                    <Clickable onClick={removeCartLineClickHandler} data-test-selector="cartline_removeLine" disabled={isQuoteCart} id="cart-btn-remove-item">
                        <span>
                            {removeLoading ?
                                <>
                                    <LoadingSpinner {...styles.spinner} className="loading-spinner" />
                                    <Typography as="span" className="remove-label">{translate("Removing")}</Typography>
                                </> :
                                <>
                                    <IconMemo {...styles.removeCartLineIcon} />
                                    <Typography as="span" className="remove-label">{translate("Remove")}</Typography>
                                </>
                            }

                        </span>
                    </Clickable>
                )}
            </GridItem>
            <GridItem {...styles.productImageGridItem} style={{
                flexDirection: enableMobileCartLineEasierDesign ? 'row' : 'column',
            }}>
                {(enableMobileCartLineEasierDesign && isMobileDevice()) ? (
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', width: '100%' }}>
                        <div style={{ flex: '0 0 20%', maxWidth: '20%', paddingTop: '20px' }}>
                            <ProductImage product={cartLine} extendedStyles={styles.productImage} />
                        </div>
                        <div style={{ flex: '1 1 80%', maxWidth: '80%' }}>
                            <Typography as="div" className="mobile-product-heading" style={{ paddingLeft: '20px', paddingTop: '20px' }}>
                                {cartLine.brand && (
                                    <ProductBrand brand={cartLine.brand} extendedStyles={styles.productBrand} />
                                )}
                                <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                            </Typography>
                        </div>
                    </div>
                ) : (
                    <>
                        <Typography as="div" className="mobile-product-heading">
                            {cartLine.brand && (
                                <ProductBrand brand={cartLine.brand} extendedStyles={styles.productBrand} />
                            )}
                            <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                        </Typography>
                        <ProductImage product={cartLine} extendedStyles={styles.productImage} />
                    </>
                )}
            </GridItem>
            <GridItem {...styles.cartLineInfoGridItem} className="right-content">
                <GridContainer {...styles.cartLineInfoContainer}>
                    <GridItem {...styles.productInfoGridItem} className="name-wrapper">
                        <GridContainer {...styles.productInfoContainer}>
                            <GridItem {...styles.productBrandAndDescriptionGridItem} className="desktop-product-heading">
                                {cartLine.brand && (
                                    <ProductBrand brand={cartLine.brand} extendedStyles={styles.productBrand} />
                                )}
                                <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                            </GridItem>
                            {!cartLine.isFixedConfiguration && cartLine.sectionOptions!.length > 0 && (
                                <GridItem {...styles.configurationGridItem}>
                                    {cartLine.sectionOptions!.map(option => (
                                        <Typography {...styles.configurationOptionText} key={option.sectionOptionId}>
                                            {`${option.sectionName}:${option.optionName}`}
                                        </Typography>
                                    ))}
                                </GridItem>
                            )}
                            <GridItem {...styles.cartProductPartNumbersGridItem}>
                                <CartProductPartNumbers
                                    productNumber={cartLine.erpNumber}
                                    customerProductNumber={cartLine.customerName}
                                    manufacturerItem={cartLine.manufacturerItem}
                                    extendedStyles={styles.cartProductPartNumbers}
                                />
                            </GridItem>
                            {showHazardousGoodIcon && cartLine.properties?.isHazardousGood === "TRUE" && (
                                <GridItem {...styles.iconGridItem}>
                                    <Icon
                                        title={hazardousIconText}
                                        src={HazardousIcon}
                                        css={css`
                                            margin-right: 0.5rem;
                                            width: 1.5rem !important;
                                            > svg {
                                                width: 1.5rem;
                                            }
                                        `}
                                    />
                                    {hazardousIconText}
                                </GridItem>
                            )}
                            {showShippingRestrictionIcon && isShowShippingIconText && (
                                <GridItem {...styles.iconGridItem}>
                                    <Icon
                                        title={shippingIconText}
                                        src={shippingIcon}
                                        css={css`
                                            margin-right: 0.5rem;
                                            width: 1.5rem !important;
                                            > svg {
                                                width: 1.5rem;
                                            }
                                        `}
                                    />
                                    {shippingIconText}
                                </GridItem>
                            )}
                            {showCalifPropIcon && cartLine.properties?.californiaprop65 !== " " &&
                                cartLine.properties?.californiaprop65?.toLowerCase() === "y" && (
                                    <GridItem {...styles.iconGridItem}>
                                        <Icon
                                            src={CalifPropIconNew}
                                            css={css`
                                            margin-right: 0.5rem;
                                            width: 1.5rem !important;
                                            > svg {
                                                width: 1.5rem;
                                            }
                                        `}
                                        />
                                        {califPropIconText}
                                    </GridItem>
                                )}
                            {showRoHsIcon && cartLine.properties?.roHS !== " " &&
                                cartLine.properties?.roHS?.toLowerCase() === "true" && (
                                    <GridItem {...styles.iconGridItem}>
                                        <Icon
                                            src={RoHsIcon}
                                            css={css`
                                            margin-right: 0.5rem;
                                            width: 1.5rem !important;
                                            > svg {
                                                width: 1.5rem;
                                            }
                                        `}
                                        />
                                        {roHsIconText}
                                    </GridItem>
                                )}
                            {errorMessages.length > 0 && (
                                <GridItem {...styles.cartLineErrorMessageGridItem}>
                                    {errorMessages.map((message, index) => (
                                        <Typography
                                            {...styles.cartLineErrorMessageText}
                                            // eslint-disable-next-line react/no-array-index-key
                                            key={index}
                                            data-test-selector="cartline_errorMessage"
                                        >
                                            {message}
                                        </Typography>
                                    ))}
                                </GridItem>
                            )}

                        </GridContainer>
                    </GridItem>
                    <GridItem {...styles.productPriceAndAvailabilityGridItem}>
                        <Hidden below="lg">
                            {!cart.cartNotPriced && (
                                <ProductPrice
                                    product={cartLine}
                                    currencySymbol={cart.currencySymbol}
                                    showSavings={true}
                                    showSavingsAmount={productSettings.showSavingsAmount}
                                    showSavingsPercent={productSettings.showSavingsPercent}
                                    extendedStyles={styles.productPrice}
                                />
                            )}
                        </Hidden>
                        {promotions.map(promotion => (
                            <Typography {...styles.promotionNameText} key={promotion.id}>
                                {promotion.name}
                            </Typography>
                        ))}
                        {showInventoryAvailability && !cartLine.quoteRequired && currentWSCountry && (
                            <ProductAvailability
                                productId={cartLine.productId!}
                                availability={cartLine.availability!}
                                unitOfMeasure={cartLine.unitOfMeasure}
                                trackInventory={cartLine.trackInventory}
                                failedToLoadInventory={cart.failedToGetRealTimeInventory}
                                extendedStyles={styles.productAvailability}
                                qtyOnHand={cartLine.qtyOnHand}
                                isDirectShip={cartLine.properties?.isDirectShip}
                                manufacturerItem={cartLine?.manufacturerItem ?? false}
                                isDirectShipFromManufacturer={cartLine.properties?.isDirectShipFromManufacturer}
                                inventoryOutofStock={cartLine.properties?.inventoryOutofStock ?? ""}
                                currentWebsiteCountry={currentWSCountry}
                                softwareProduct={cartLine.properties?.software_Product ?? false}
                                calibrationService={cartLine.properties?.calibrationService ?? false}
                            />
                        )}
                        <Hidden above="md">
                            {!cart.cartNotPriced && (
                                <ProductPrice
                                    product={cartLine}
                                    currencySymbol={cart.currencySymbol}
                                    showSavings={true}
                                    showSavingsAmount={productSettings.showSavingsAmount}
                                    showSavingsPercent={productSettings.showSavingsPercent}
                                    extendedStyles={styles.productPrice}
                                />
                            )}
                        </Hidden>
                        <StyledWrapper {...styles.quantityGridItem}>
                            <CartLineQuantity
                                cart={cart}
                                editable={true}
                                onQtyOrderedChange={qtyOrderedChangeHandler}
                                extendedStyles={styles.quantity}
                            />
                        </StyledWrapper>

                        {showNcNrIcon && cartLine.properties?.ncnr === "true" ? (
                            <StyledWrapper {...styles.nonCancellableNonReturnableContainer}>
                                <Typography {...styles.nonCancellableNonReturnableText}>
                                    <Link {...styles.nonCancellableNonReturnableAnchor} href={String(siteMessage("NonCancellableNonReturnableLink"))} title={String(siteMessage("NonCancellableNonReturnableText"))}>
                                        {siteMessage("NonCancellableNonReturnableText")}
                                    </Link>
                                </Typography>
                            </StyledWrapper>
                        ) : ("")}
                    </GridItem>
                    {cart.showCostCode && !cartLine.isPromotionItem && (
                        <GridItem {...styles.costCodeGridItem}>
                            <Typography {...styles.costCodeLabelText}>{cart.costCodeLabel}</Typography>
                            {cart.canEditCostCode ? (
                                <Select
                                    value={cartLine.costCode}
                                    onChange={costCodeChangeHandler}
                                    {...styles.costCodeSelect}
                                >
                                    {cart.costCodes?.map(costCode => (
                                        <option key={costCode.costCode} value={costCode.costCode}>
                                            {costCode.description}
                                        </option>
                                    ))}
                                </Select>
                            ) : (
                                <Typography {...styles.costCodeText}>{cartLine.costCode}</Typography>
                            )}
                        </GridItem>
                    )}
                    {showLineNotes && !cartLine.isPromotionItem && cart.properties["isPunchout"] === undefined && (
                        <GridItem {...styles.cartLineNotesGridItem}>
                            <CartLineNotes
                                cart={cart}
                                editable={true}
                                onNotesChange={notesChangeHandler}
                                extendedStyles={styles.cartLineNotes}
                            />
                        </GridItem>
                    )}
                </GridContainer>
            </GridItem>
            {showHazardousGoodIcon && cartLine.properties?.isHazardousGood === "TRUE" && (
                <GridItem {...styles.cartLineHazmatMessageGridItem}>
                    <Typography as="div" {...styles.cartLineHazmatMessage}>
                        <p><b>Warning:</b> This product contains hazardous materials (HazMat). Handle with care and avoid direct contact. Open in a well-ventilated area and do not store in airtight containers. Follow all applicable safety and carrier guidelines to ensure safe handling and compliance.</p>
                    </Typography>
                </GridItem>
            )}
            {cartLine.properties?.shippingClassification === "ML" && (
                <GridItem {...styles.cartLineShipFreightGridItem}>
                    <Typography as="div" {...styles.cartLineShipFreightMessage}>
                        <p><b>Important:</b> This product must ship via freight carrier due to its size, weight, or handling requirements. Additional transit time, delivery coordination, and shipping costs may apply.</p>
                    </Typography>
                </GridItem>
            )}
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withCartLine(CartLineCardExpanded));
